import React, { FC, ReactNode, useContext, useEffect, useState } from 'react'

import { ChevronDownIcon, LoaderCircle, PlusIcon } from 'lucide-react'

import { Button } from '../../../../../ui/button'
import { PaymentStatus } from '../../../../../../pages/receipt/components/PaymentStatus/PaymentStatus'
import { PaymentActions } from '../../PaymentActions/PaymentActions'
import { PaymentAllActions } from '../../PaymentAllActions/PaymentAllActions'
import { PriceItem } from '../../../../../common/price/PriceItem'
import { ClassesSkeleton } from '../../empty/LineItemsEmpty/LineItemsEmpty'

import { cn } from '../../../../../../lib/utils'
import {
  IEventTeamMemberInterface,
  useEventRiderTeamMembers,
} from '../../../../../../hooks/useEventRiderTeamMembers'
import { usePaymentActions } from '../../../../../../hooks/usePaymentActions'
import { getRiderTeamMembersByRole } from '../../../../../../helpers/riders'
import useToasterHelper from '../../../../../../helpers/ToasterHelper'
import {
  getCurrency,
  getPaymentMethodsFormatted,
  isPaymentPending,
  isRefundPending,
} from '../../../../../../helpers/payment'
import { getRegistrationEventDataInDb } from '../../../../../../helpers/registration-on-event'
import { getAllRegistrationFees } from '../../../../../../helpers/registrationFees'

import { useAppSelector } from '../../../../../../store/hooks'
import { selectProfileData } from '../../../../../../store/user/userSlice'

import FirestoreService from '../../../../../../services/firestoreService'

import {
  HORSE_NAME_DEFAULT,
  IHorseSelected,
  IRegistrationByDayInterface,
} from '../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IPaymentsCards, IUserInterface } from '../../../../../../models/users/user.interface'
import { IEventReviewPublish } from '../../../../../../models/event-review-publish/event-review-publish.interface'
import { IRiderTeamMemberInterface } from '../../../../../../models/rider-team-member/riderTeamMember.interface'
import { PAYMENT_STATUS } from '../../../../../../types/payment'

import { MEMBER_ROLES } from '../../../../../../const/ui/ui-register.const'
import { MESSAGES_CONST } from '../../../../../../const/messages-const'
import { CONST } from '../../../../../../const/const'
import { MODAL_CONSTS } from '../../../../../../const/modal-const'
import { HandleModalContext } from '../../../../../../layout/mainlayout/MainLayout'
import { REGISTRAR_TABS } from '../../../RegistrarInfoModal/RegistrarInfoModal'
import { RIDER_TABS } from '../../../RiderInfoModal/RiderInfoModal'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface TeamMemberProps {
  eventTeamMembers: IEventTeamMemberInterface
  rider: IRegistrationByDayInterface
  role: string
}

const TeamMember: FC<TeamMemberProps> = ({ eventTeamMembers, rider, role }) => {
  const { teamMembersNames } = getRiderTeamMembersByRole(eventTeamMembers, rider, role)

  return teamMembersNames.size > 0 ? (
    <p className="text-xs text-black/50">
      {[...teamMembersNames].join(', ')} - {role}
    </p>
  ) : null
}

interface ClassesTabProps {
  isOpenedDefault?: boolean
  type?: 'overview' | 'registrar'
  className?: string
  isCollapsible?: boolean
  registrarId: string | null
  registrationId: string
  eventId: string
  organizer: IUserInterface | null
  user: IUserInterface | null
  publishedEvent: IEventReviewPublish | null
  onActionClick?: () => void
  actionIcon?: ReactNode
}

export const ClassesTab: FC<ClassesTabProps> = ({
  isOpenedDefault,
  type,
  className,
  isCollapsible,
  registrarId,
  registrationId,
  eventId,
  organizer,
  user,
  publishedEvent,
  onActionClick,
  actionIcon,
}) => {
  const handleModalContext = useContext(HandleModalContext)
  const toastFunctions = useToasterHelper()

  const { eventTeamMembers, eventTeamMembersLoading, getEventRiderTeamMembers } =
    useEventRiderTeamMembers(eventId || '')
  const { onScratch, onRefund, onPayByCard, onMarkPaid, onRecipientClick, onUndoMarkMaid } =
    usePaymentActions()

  const profileData = useAppSelector(selectProfileData)

  const [loading, setLoading] = useState(true)
  const [isOpen, setIsOpen] = useState(!isCollapsible || isOpenedDefault)
  const [showAdjustField, setShowAdjustField] = useState<number | null>(null)
  const [showRefundField, setShowRefundField] = useState<number | null>(null)
  const [filteredRiders, setFilteredRiders] = useState<IRegistrationByDayInterface[]>([])
  const [riders, setRiders] = useState<IRegistrationByDayInterface[]>([])
  const [paymentsCards, setPaymentsCards] = useState<IPaymentsCards>({ CARDS: [], ACH: [] })

  useEffect(() => {
    const sorted = riders.sort(
      (a, b) => new Date(b.created as Date).getTime() - new Date(a.created as Date).getTime()
    )

    let filtered
    if (type === 'overview') {
      filtered = sorted.filter(
        (rider) =>
          isPaymentPending(rider.isScratched, rider.paymentStatus) ||
          isRefundPending(
            rider.isScratched,
            rider.refundAmountAdded,
            rider.paymentStatus,
            rider.paymentType
          )
      )
    } else {
      filtered = sorted
    }
    setFilteredRiders(filtered)
  }, [riders, type])

  useEffect(() => {
    if (!user?.id) return

    const getData = async () => {
      try {
        setLoading(true)

        const { registrationsByDay } = await getRegistrationEventDataInDb({
          registrationId,
          ownerId: user.id,
          type: ['classes'],
        })

        let userEmail
        // If it is an organizer, we show the riders' cards; if it is the registrar, we show the registrar's cards.
        if (organizer?.id === profileData.id) userEmail = user.userEmail
        else userEmail = profileData.userEmail

        const payments = await getPaymentMethodsFormatted(userEmail, organizer?.userStripeAccountId)

        setRiders(registrationsByDay)
        setPaymentsCards(payments)
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }
    getData().then()
  }, [
    organizer?.id,
    organizer?.userStripeAccountId,
    profileData.id,
    profileData.userEmail,
    registrationId,
    user?.id,
    user?.userEmail,
  ])

  const horses: IHorseSelected[] = []
  filteredRiders?.forEach((rider) => {
    rider?.horsesSelected?.forEach((item) => {
      const isExist = horses?.find((horse) => horse.name === item.name)
      if (!isExist && item.id !== HORSE_NAME_DEFAULT) horses.push(item)
    })
  })

  const isScratched = filteredRiders?.filter((rider) => rider.isScratched)

  const refundPending = (items: IRegistrationByDayInterface[]) => {
    return (
      items?.filter((rider) =>
        isRefundPending(
          rider.isScratched,
          rider.refundAmountAdded,
          rider.paymentStatus,
          rider.paymentType
        )
      )?.length || 0
    )
  }

  const paymentPending = filteredRiders?.filter((rider) =>
    isPaymentPending(rider.isScratched, rider.paymentStatus)
  )

  const haveScratched = filteredRiders?.filter((rider) => !rider.isScratched) ?? []

  const haveNotPaid =
    filteredRiders?.filter((rider) => rider.paymentStatus === PAYMENT_STATUS.DEFAULT) ?? []

  const onSaveAdjust = async (adjustItem: IRegistrationByDayInterface, adjustedValue: number) => {
    try {
      await FirestoreService.updateItem(COLLECTIONS.REGISTRATION_BY_DAY.NAME, adjustItem.id, {
        registrationPrice: adjustedValue,
        originalRegistrationPrice:
          adjustItem.originalRegistrationPrice ?? adjustItem.registrationPrice,
      })

      const { registrationsByDay } = await getRegistrationEventDataInDb({
        registrationId,
        ownerId: user?.id,
        type: ['classes'],
      })
      setRiders(registrationsByDay)

      setShowAdjustField(null)

      toastFunctions.success({
        message: `Adjusted successfully`,
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onSaveRefund = async (refundItems: IRegistrationByDayInterface[], refundValue?: number) => {
    if (organizer?.id && user?.id) {
      const response = await onRefund({
        refundRegistrationsByDayItems: refundItems,
        refundFeesItems: [],
        refundTicketsItems: [],
        registrationDocId: registrationId,
        publishedEvent: publishedEvent,
        ownerId: user?.id,
        refundValue: refundValue,
      })
      setShowRefundField(null)
      if (response?.registrationsByDayUpdated) onUpdate(response.registrationsByDayUpdated)
    }
  }

  const onSaveAssign = async (
    assignItem: IRegistrationByDayInterface,
    teamMember: IRiderTeamMemberInterface
  ) => {
    try {
      await FirestoreService.updateItem(COLLECTIONS.REGISTRATION_BY_DAY.NAME, assignItem.id, {
        recipientId: teamMember?.teamMemberId || null,
        recipientName: teamMember?.teamMemberName || null,
        recipientProfilePicture: teamMember?.teamMemberProfilePicture || null,
      })

      const { registrationsByDay } = await getRegistrationEventDataInDb({
        registrationId,
        ownerId: user?.id,
        type: ['classes'],
      })
      setRiders(registrationsByDay)

      toastFunctions.success({
        message: `Assigned successfully`,
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDeleteItem = async (id: string) => {
    try {
      await FirestoreService.deleteItem(COLLECTIONS.REGISTRATION_BY_DAY.NAME, id)

      const { registrationsByDay } = await getRegistrationEventDataInDb({
        registrationId,
        ownerId: user?.id,
        type: ['classes'],
      })

      setRiders(registrationsByDay)

      toastFunctions.success({
        message: 'Class Deleted successfully',
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onAddClasses = () => {
    handleModalContext?.handleModal(true, MODAL_CONSTS.ADD_CLASSES, {
      ...handleModalContext?.dataToPassOn,
      eventId: eventId,
      registrationDocId: registrationId,
      user: user,
      onSave: async (newClasses: IRegistrationByDayInterface[]) => {
        await getEventRiderTeamMembers()
        setRiders([...riders, ...newClasses])
      },
    })
  }

  const onUpdate = (registrationsByDayUpdated: IRegistrationByDayInterface[]) => {
    const updated = riders.map((rider) => {
      const current = registrationsByDayUpdated?.find((item) => item.id === rider.id)
      if (current?.id) {
        return { ...rider, ...current }
      } else {
        return rider
      }
    })
    setRiders(updated)
  }

  const currency = getCurrency(publishedEvent?.EventPaymentSettings?.currency)

  if (loading) return <ClassesSkeleton />

  return (
    <div className={cn('rounded-lg border border-black/10 px-7 py-3', className)}>
      <div className="flex gap-2 flex-wrap items-center justify-between">
        <div>
          <p className="text-base font-medium">Classes</p>
          {type === 'overview' ? (
            <p className="text-xs text-black/50">
              {filteredRiders?.[0] ? `${filteredRiders.length} issues` : 'No issues'}
            </p>
          ) : (
            <p className="text-xs text-black/50">
              {filteredRiders?.length || 0} classes, {horses.length || 0} horses,{' '}
              {isScratched?.length || 0} scratch
            </p>
          )}
        </div>
        <div className="text-right flex gap-3 items-center justify-end ml-auto">
          {((type === 'registrar' && !filteredRiders?.[0]) ||
            (type !== 'overview' && !filteredRiders?.[0])) && (
            <Button
              variant="link"
              type="button"
              className="rounded-full  w-[30px] h-[30px] text-black bg-black/5 hover:opacity-70 transition-all"
              onClick={onAddClasses}
            >
              <PlusIcon className="w-4" />
            </Button>
          )}
          {type === 'overview' ? (
            <Button
              variant="link"
              type="button"
              className="rounded-full p-0 hover:opacity-70"
              onClick={onActionClick}
            >
              {actionIcon}
            </Button>
          ) : (
            filteredRiders?.[0] && (
              <>
                <div className="flex gap-2 items-center justify-end flex-wrap">
                  {refundPending(filteredRiders) > 0 && (
                    <div className="flex items-center text-sm text-PurpleThemeColor bg-PurpleThemeColor/5 px-3 py-1 rounded-full">
                      {refundPending(filteredRiders)} refund pending
                    </div>
                  )}
                  {paymentPending && paymentPending.length > 0 && (
                    <div className="flex items-center text-sm text-SeabiscuitRedThemeColor bg-SeabiscuitRedThemeColor/5 px-3 py-1 rounded-full">
                      {paymentPending.length} payment pending
                    </div>
                  )}
                </div>
                <PaymentAllActions
                  type="class"
                  haveScratched={haveScratched?.length > 0}
                  isRefundPending={refundPending(filteredRiders) > 0}
                  haveNotPaid={haveNotPaid.length > 0}
                  paymentsCards={paymentsCards}
                  organizer={organizer}
                  setPaymentsCards={setPaymentsCards}
                  user={user}
                  eventReviewPublish={publishedEvent}
                  onScratch={async () => {
                    const response = await onScratch({
                      registrationDocId: registrationId,
                      publishedEvent: publishedEvent,
                      user: user,
                      registrationsByDayScratchedItems: filteredRiders.filter(
                        (rider) => !rider.isScratched
                      ),
                      feesScratchedItems: [],
                      ticketsScratchedItems: [],
                      isScratched: false,
                    })
                    if (response?.registrationsByDayUpdated)
                      onUpdate(response.registrationsByDayUpdated)
                  }}
                  onRefund={async () => {
                    const items = filteredRiders.filter(
                      (rider) =>
                        rider.isScratched &&
                        !rider.refundAmountAdded &&
                        rider.paymentStatus === PAYMENT_STATUS.PAID
                    )
                    await onSaveRefund(items)
                  }}
                  onMarkPaidAction={async (values) => {
                    if (user) {
                      const response = await onMarkPaid({
                        markPaidRegistrationsByDayItems: filteredRiders.filter(
                          (rider) => !rider.paymentStatus && !rider.isScratched
                        ),
                        markPaidFeesItems: [],
                        markPaidTicketsItems: [],
                        paymentType: values.type,
                        checkMemo: values.checkMemo,
                        checkNumber: values.checkNumber,
                        registrationDocId: registrationId,
                        user: user,
                        organizerId: organizer?.id || null,
                      })
                      if (response?.registrationsByDayUpdated)
                        onUpdate(response.registrationsByDayUpdated)
                    }
                  }}
                  onPayByCard={async (card) => {
                    if (user && registrarId) {
                      const response = await onPayByCard({
                        registrationsByDayItems: filteredRiders.filter(
                          (rider) => !rider.paymentStatus && !rider.isScratched
                        ),
                        feeItems: [],
                        ticketItems: [],
                        card: card,
                        registrationDocId: registrationId,
                        user: user,
                        publishedEvent: publishedEvent,
                      })

                      if (response?.registrationsByDayUpdated)
                        onUpdate(response.registrationsByDayUpdated)
                    }
                  }}
                  onAddNew={onAddClasses}
                />
              </>
            )
          )}
          {isCollapsible && (
            <Button
              variant="link"
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                'rounded-full duration-200 w-[30px] h-[30px] flex items-center justify-center shrink-0 hover:opacity-70 transition-all',
                isOpen
                  ? 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rotate-180'
                  : 'text-black bg-black/5'
              )}
            >
              <ChevronDownIcon className="size-4" />
            </Button>
          )}
        </div>
      </div>
      {isOpen && (
        <div
          className={cn(
            (!isCollapsible || isOpen) && filteredRiders?.[0] && 'mt-3 border-t border-t-black/10'
          )}
        >
          {filteredRiders?.[0]
            ? filteredRiders?.map((rider, index) => {
                const divisionName = publishedEvent?.EventFees?.registrationFees?.find(
                  (fee) => fee.uuid === rider.divisionUuid
                )?.name

                const fees = getAllRegistrationFees(
                  publishedEvent?.EventFees?.registrationFees || []
                )
                const fee = fees?.find((fee) => fee.uuid === rider.uuid)

                return (
                  <div
                    key={index}
                    className={cn(
                      'flex gap-2 flex-wrap items-center justify-between py-4',
                      filteredRiders.length > index + 1 && 'border-b border-b-black/10'
                    )}
                  >
                    <div>
                      <p className="text-sm flex items-center gap-2 flex-wrap mb-1">
                        <span className={cn(rider.isScratched && 'line-through')}>
                          {rider.registrationByDayName}
                        </span>
                        {fee?.order ? (
                          <span
                            className={cn(
                              'text-xs text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rounded-full py-1 leading-none px-3',
                              rider.isScratched
                                ? 'text-black/30 bg-black/5'
                                : 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5'
                            )}
                          >
                            {fee.order}
                          </span>
                        ) : null}
                        {rider.isQualify && (
                          <span className="text-xs leading-none border border-SeabiscuitDark200ThemeColor px-3 py-1 rounded-full">
                            Bonus Fee
                          </span>
                        )}
                      </p>
                      {divisionName && (
                        <p className="text-xs text-black/50 mb-1">Division: {divisionName}</p>
                      )}
                      <div className="text-xs text-black/50 mb-1">
                        <PriceItem
                          isRefunded={rider.refundAmountAdded}
                          amountRefunded={rider.amountRefunded}
                          price={rider.registrationPrice || 0}
                          originalPrice={rider.originalRegistrationPrice || 0}
                          qualifyFee={rider.qualifyFee}
                          isQualify={rider.isQualify}
                          qualifyFeeName={rider.qualifyFeeName}
                          showAdjustField={showAdjustField === index}
                          onCancelAdjust={() => setShowAdjustField(null)}
                          onSaveAdjust={(value) => onSaveAdjust(rider, value)}
                          showRefundField={showRefundField === index}
                          onCancelRefund={() => setShowRefundField(null)}
                          onSaveRefund={(value) => onSaveRefund([rider], value)}
                          currency={currency}
                        />
                      </div>
                      {rider.horsesSelected?.length > 0 ? (
                        <p className="text-xs text-black/50 mb-1">
                          {[...rider.horsesSelected.map(({ name }) => name)]?.join(', ')} - Horse
                          {rider.horsesSelected.length > 1 ? 's' : ''}
                        </p>
                      ) : (
                        <p className="text-xs text-black/50 mb-1">No Horse</p>
                      )}
                      {eventTeamMembersLoading ? (
                        <p className="text-xs text-black/50">
                          <LoaderCircle className="w-[12px] h-[12px] animate-spin" />
                        </p>
                      ) : (
                        Object.values(MEMBER_ROLES)
                          .filter((role) => role !== MEMBER_ROLES.RIDER)
                          .map((role) => (
                            <TeamMember
                              key={role}
                              eventTeamMembers={eventTeamMembers}
                              rider={rider}
                              role={role}
                            />
                          ))
                      )}
                    </div>
                    <div className="text-right ml-auto">
                      {type !== 'overview' && (
                        <PaymentActions
                          isRefundPending={refundPending([rider]) > 0}
                          eventTeamMembers={eventTeamMembers}
                          paymentStatus={rider.paymentStatus}
                          paymentType={rider.paymentType}
                          isScratched={rider.isScratched}
                          isRefunded={rider.refundAmountAdded ?? false}
                          paymentsCards={paymentsCards}
                          organizer={organizer}
                          user={user}
                          eventReviewPublish={publishedEvent}
                          setPaymentsCards={setPaymentsCards}
                          onScratch={async (type) => {
                            const response = await onScratch({
                              registrationDocId: registrationId,
                              publishedEvent: publishedEvent,
                              user: user,
                              registrationsByDayScratchedItems: [rider],
                              feesScratchedItems: [],
                              ticketsScratchedItems: [],
                              isScratched: type,
                            })
                            if (response?.registrationsByDayUpdated)
                              onUpdate(response.registrationsByDayUpdated)
                          }}
                          onMarkPaidAction={async (values) => {
                            if (user) {
                              const response = await onMarkPaid({
                                markPaidRegistrationsByDayItems: [rider],
                                markPaidFeesItems: [],
                                markPaidTicketsItems: [],
                                paymentType: values.type,
                                checkMemo: values.checkMemo,
                                checkNumber: values.checkNumber,
                                registrationDocId: registrationId,
                                user: user,
                                organizerId: organizer?.id || null,
                              })
                              if (response?.registrationsByDayUpdated)
                                onUpdate(response.registrationsByDayUpdated)
                            }
                          }}
                          onAdjust={() => setShowAdjustField(index)}
                          onRefund={() => setShowRefundField(index)}
                          onSaveAssign={(teamMember) => onSaveAssign(rider, teamMember)}
                          onPayByCard={async (card) => {
                            if (user && registrarId) {
                              const response = await onPayByCard({
                                registrationsByDayItems: [rider],
                                feeItems: [],
                                ticketItems: [],
                                card: card,
                                registrationDocId: registrationId,
                                user: user,
                                publishedEvent: publishedEvent,
                              })
                              if (response?.registrationsByDayUpdated)
                                onUpdate(response.registrationsByDayUpdated)
                            }
                          }}
                          onDelete={async () => await onDeleteItem(rider.id || '')}
                          registrationDocId={registrationId}
                        />
                      )}
                      <div
                        className={cn(
                          'flex items-center text-sm',
                          isRefundPending(
                            rider.isScratched,
                            rider.refundAmountAdded,
                            rider.paymentStatus,
                            rider.paymentType
                          )
                            ? 'text-PurpleThemeColor bg-PurpleThemeColor/5'
                            : isPaymentPending(rider.isScratched, rider.paymentStatus)
                              ? 'text-SeabiscuitRedThemeColor bg-SeabiscuitRedThemeColor/5'
                              : ' text-black/50',
                          type === 'overview' ? 'rounded-full py-1 px-3' : 'bg-transparent'
                        )}
                      >
                        <PaymentStatus
                          isAdjusted={!!rider.originalRegistrationPrice}
                          isRefunded={rider.refundAmountAdded || false}
                          isScratched={rider.isScratched}
                          paymentStatus={rider.paymentStatus}
                          scratchedDate={rider.scratchedDate || null}
                          refundedDate={rider.refundedDate || null}
                          paidDate={rider.paidDate || null}
                          paymentType={rider.paymentType}
                          ownerId={rider.riderId || null}
                          ownerName={rider.riderName || null}
                          recipientId={rider.recipientId || null}
                          recipientName={rider.recipientName || null}
                          userId={user?.id}
                          onRecipientClick={(value) =>
                            onRecipientClick({
                              eventId,
                              registrarId,
                              registrationId,
                              defaultActiveTab: REGISTRAR_TABS.TEAM.value,
                              defaultOpenedPrimaryAccordion: value,
                              defaultOpenedSecondaryAccordion: RIDER_TABS.CLASSES
                                .value as 'classes',
                            })
                          }
                          onUndoMarkMaid={async () => {
                            if (rider.id && user) {
                              const response = await onUndoMarkMaid({
                                eventId: eventId,
                                registrationId: registrationId,
                                paymentType: rider.paymentType,
                                registrationsByDay: [rider],
                                fees: [],
                                tickets: [],
                                user: user,
                                publishedEvent: publishedEvent,
                              })

                              if (response?.registrationsByDayUpdated)
                                onUpdate(response.registrationsByDayUpdated)
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })
            : type === 'registrar' && (
                <div className="pt-4 mt-4 border-t border-t-black/10">
                  <div className=" text-black/50 text-xs bg-black/[2%] h-[112px] rounded-lg flex items-center justify-center p-4">
                    No classes have been added to this entry
                  </div>
                </div>
              )}
        </div>
      )}
    </div>
  )
}
